function toDateOrNull(value) {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

function mostRecentDate(...values) {
  const parsed = values.map(toDateOrNull).filter(Boolean)
  if (!parsed.length) return null
  return parsed.reduce((latest, current) => (current > latest ? current : latest), parsed[0])
}

export function getStationType(station) {
  return (station?.metadata?.station_type || station?.station_type || station?.stationType || station?.type || 'full').toLowerCase()
}

export function isSensorStation(station) {
  return getStationType(station) === 'sensor'
}

export function getSystemStatus(station) {
  const status = station?.systemStatus || station?.system_status || station?.stationStatus || station?.station_status || null
  if (!status) {
    return {
      box: null,
      box_last_ping_time: null,
      box_state_change_time: null,
      video: null,
      video_last_ping_time: null,
      video_state_change_time: null,
      weather: null,
      weather_last_ping_time: null,
      weather_state_change_time: null,
      gps: null,
      gps_last_ping_time: null,
      gps_state_change_time: null,
      modem: null,
      modem_last_ping_time: null,
      modem_state_change_time: null,
      battery: null,
      battery_last_ping_time: null,
      battery_state_change_time: null,
    }
  }

  return {
    box: status.box ?? null,
    box_last_ping_time: status.box_last_ping_time ?? null,
    box_state_change_time: status.box_state_change_time ?? null,
    video: status.video ?? null,
    video_last_ping_time: status.video_last_ping_time ?? null,
    video_state_change_time: status.video_state_change_time ?? null,
    weather: status.weather ?? null,
    weather_last_ping_time: status.weather_last_ping_time ?? null,
    weather_state_change_time: status.weather_state_change_time ?? null,
    gps: status.gps ?? null,
    gps_last_ping_time: status.gps_last_ping_time ?? null,
    gps_state_change_time: status.gps_state_change_time ?? null,
    modem: status.modem ?? null,
    modem_last_ping_time: status.modem_last_ping_time ?? null,
    modem_state_change_time: status.modem_state_change_time ?? null,
    battery: status.battery ?? null,
    battery_last_ping_time: status.battery_last_ping_time ?? null,
    battery_state_change_time: status.battery_state_change_time ?? null,
  }
}

export function getStationHeartbeat(station) {
  const systemStatus = getSystemStatus(station)
  const commissionedPing = toDateOrNull(systemStatus.box_last_ping_time)
  if (!commissionedPing) return null

  return (
    mostRecentDate(
      commissionedPing,
      systemStatus.box_last_ping_time,
      systemStatus.video_last_ping_time,
      systemStatus.weather_last_ping_time,
      systemStatus.gps_last_ping_time,
      systemStatus.modem_last_ping_time,
      systemStatus.battery_last_ping_time,
      systemStatus.box_state_change_time,
      systemStatus.video_state_change_time,
      systemStatus.weather_state_change_time,
      systemStatus.gps_state_change_time,
      systemStatus.modem_state_change_time,
      systemStatus.battery_state_change_time
    ) || commissionedPing
  )
}

export function getDerivedStationState(station) {
  const systemStatus = getSystemStatus(station)
  const commissionedPing = toDateOrNull(systemStatus.box_last_ping_time)
  const heartbeat = getStationHeartbeat(station)
  const now = new Date()
  const diffMinutes = heartbeat ? (now - heartbeat) / (1000 * 60) : Number.POSITIVE_INFINITY

  const commissioned = !!commissionedPing
  const sensorStation = isSensorStation(station)
  const boxOnline = systemStatus.box ?? station?.isOnline ?? station?.is_online ?? station?.online ?? false
  const hasVideo = systemStatus.video ?? station?.hasVideo ?? station?.has_video ?? station?.video ?? false
  const videoRequired = !sensorStation

  let statusText = 'Not Commissioned'
  let statusColor = 'bg-gray-500'

  if (commissioned) {
    if (!boxOnline || diffMinutes >= 30) {
      statusText = 'Offline'
      statusColor = 'bg-red-500'
    } else if ((videoRequired && !hasVideo) || diffMinutes >= 5) {
      statusText = 'Issue'
      statusColor = 'bg-orange-500'
    } else {
      statusText = 'Online'
      statusColor = 'bg-green-500'
    }
  }

  return {
    systemStatus,
    heartbeat,
    heartbeatIso: heartbeat ? heartbeat.toISOString() : null,
    commissioned,
    boxOnline: !!boxOnline,
    hasVideo: !!hasVideo,
    videoRequired,
    isOnline: commissioned && !!boxOnline && diffMinutes < 30,
    statusText,
    statusColor,
  }
}

export function normalizeStationStatusFields(station) {
  if (!station || typeof station !== 'object') return station
  const derived = getDerivedStationState(station)

  return {
    ...station,
    systemStatus: derived.systemStatus,
    isCommissioned: derived.commissioned,
    isOnline: derived.isOnline,
    online: derived.isOnline,
    hasVideo: derived.hasVideo,
    video: derived.hasVideo,
    stationPing: derived.heartbeatIso || null,
    timestamp: derived.heartbeatIso || station?.timestamp || null,
    statusText: derived.statusText,
    statusColor: derived.statusColor,
    videoRequired: derived.videoRequired,
  }
}
